import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
} from "recharts";
import { fmtCurrency, fmtCurrencyK } from "../lib/formatters";
import { DelayedTooltipContent } from "../lib/DelayedTooltip";
import { useAutoDismiss } from "../hooks/useAutoDismiss";
import FullFundingCalculationTable from "./FullFundingCalculationTable";

// 2026-09-27 (Dave) — Click-gated inner tooltip renderer, mirrors the
// pattern used by the Dashboard's Reserve Activity chart
// (<ActivityTooltip>). Returns null unless (a) the tooltip is active
// AND (b) the currently-hovered column matches the year the user
// explicitly clicked (`revealedLabel === label`). Everything else
// hover-only renders the light-gray column tint via the chart's
// `cursor` prop. Rendered inside <DelayedTooltipContent inner={…}/>
// so the shared reveal-delay + auto-dismiss timers run against the
// same platform_settings knobs as every other chart.
function FundingAnalysisTooltip({ active, payload, label, revealedLabel }) {
  if (!active || !payload || payload.length === 0) return null;
  // Hover delay (DelayedTooltipContent) or a click (forceLabel) reveals details.
  return (
    <div
      className="bg-white text-[11px]"
      style={{
        border: "1px solid #1C4E80",
        borderRadius: 3,
        padding: "4px 10px 5px",
        minWidth: 200,
        pointerEvents: "none",
        lineHeight: 1.3,
      }}
      data-testid="funding-analysis-tooltip"
    >
      <div style={{ fontWeight: 700, color: "#1C4E80", marginBottom: 2 }}>
        Year {label}
      </div>
      {payload.map((p, i) => {
        if (p.value == null || Number.isNaN(p.value)) return null;
        const isPct = p.name === "Projected Funding %" || p.name === "Actual Funding %";
        const val = isPct ? `${Math.round(Number(p.value))}%` : fmtCurrency(p.value);
        return (
          <div
            key={i}
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 14,
              color: p.color,
            }}
          >
            <span>{p.name}</span>
            <span className="font-mono tabular-nums">{val}</span>
          </div>
        );
      })}
    </div>
  );
}

// Step 13 · 2026-08-05 (Dave)
// "Funding Level Analysis" — the first of two on-demand charts reached via
// the "More" button on the Reserve Activity by Year panel.
//
// Dual axis:
//   • LEFT ($):  Reserve Balance ($) columns  +  Fully Funded Balance ($) line
//   • RIGHT (%): Actual Funding %  line  +  Target Funding % reference line
//
// Background: three shaded risk bands on the RIGHT-axis percentage scale
// (Red 0–33 · Yellow 33–66 · Green 66–100), exact palette matches the
// existing Funding Level gauge on the Dashboard.
//
// Renders as a full-screen modal overlay so the chart can breathe. Horizon
// context-sensitive: slices calc.projection[0..horizon] and the matching
// full_funding_by_year[0..horizon] series added to CalculationOut.
// Spacers above/below the panel legend (percent of panel height); sum keeps the plot ~15% shorter.
const PANEL_LEGEND_GAP_TOP = "3.5%";
const PANEL_LEGEND_GAP_BOTTOM = "6.5%";

export default function FundingLevelAnalysisChart({
  open,
  onClose,
  calc,
  variables,
  association,
  horizon,
  variant = "modal", // "panel" = inline Illustrations-tab panel (Dave 2026-10-16)
  autoDismiss = true,
}) {
  const isPanel = variant === "panel";
  const [view, setView] = useState("chart"); // 'chart' | 'table'
  const [qcLensTop, setQcLensTop] = useState(0);
  // 2026-02-27 (Dave) — Local horizon selector inside the modal so the
  // user can flex the Reserve Balance vs Fully Funded comparison across
  // 5 / 10 / 15 / 20 / 25 / 30 year windows without leaving the modal.
  // Seeded from the parent's horizon (whatever's showing on the Dashboard)
  // and re-seeded on each open so the modal always mirrors the dashboard
  // starting state.
  const [localHorizon, setLocalHorizon] = useState(horizon);
  useEffect(() => { if (open || isPanel) setLocalHorizon(horizon); }, [open, isPanel, horizon]);

  // 2026-02-19 (Dave) — auto-dismiss after `hover_visible_seconds` (6 s
  // default) so click-popups feel consistent with hover tooltips.
  useAutoDismiss(open && autoDismiss, onClose);

  // 2026-09-27 (Dave) — Click-to-reveal parity with the Dashboard
  // Reserve Activity chart. Hover shows a light-gray column tint (via
  // Recharts' `cursor` prop); a click commits that column's year into
  // `revealedLabel`; the detail tooltip only renders for the clicked
  // column. Second click on same column dismisses; click on a different
  // column swaps. Mirrors ActivityChart's revealedLabel state machine.
  const [revealedLabel, setRevealedLabel] = useState(null);
  const handleFAChartClick = (state) => {
    if (!state || state.activeLabel === undefined || state.activeLabel === null) return;
    setRevealedLabel((prev) => (prev === state.activeLabel ? null : state.activeLabel));
  };
  // Reset the click-reveal every time the modal opens or the horizon
  // window changes, so the modal always opens in the "no column
  // committed" state.
  useEffect(() => { setRevealedLabel(null); }, [open, localHorizon]);

  // Esc-to-close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Reset to chart view whenever the modal reopens.
  useEffect(() => {
    if (open) setView("chart");
  }, [open]);

  // iPhone QC lens fix — anchor the absolute-positioned overlay at the
  // current scroll position on open so the modal sits over whatever the
  // user was looking at (not at the top of the document, which the QC-
  // lens CSS defaults to for `position: absolute`).
  //
  // Detection uses the `.qc-lens-active` class on <html> — media queries
  // like `matchMedia("(max-width: 767px)")` stop matching once the QC
  // lens hack rewrites the viewport meta to `width=1440`.
  useEffect(() => {
    if (!open) return;
    if (document.documentElement.classList.contains("qc-lens-active")) {
      setQcLensTop(window.scrollY || 0);
    }
  }, [open]);

  if ((!open && !isPanel) || !calc || !variables) return null;

  const targetPct = (variables.target_pct || 0) * 100;
  const rows = calc.projection.slice(0, localHorizon);

  // Peak-convention FFB series (same helper the FFC table uses so the
  // chart's Fully Funded line reads identically to the FFC's bottom
  // row — no numerical divergence between the two views).
  const ffbByYear = calc.ffb_peak_by_year || []; // server-computed

  const dYears = calc.display?.years || [];
  const data = rows.map((r, idx) => {
    const fullFunded = ffbByYear[idx] || 0;
    const actualPct = dYears[idx]?.funding_pct ?? 0; // server-computed
    // Shortfall = max(0, fully-funded × target_pct − ending), server-computed.
    const shortfall = dYears[idx]?.shortfall || 0;
    return {
      year: r.year,
      reserveBalance: Math.round(r.ending),
      fullyFunded: Math.round(fullFunded),
      actualFundingPct: Number(actualPct.toFixed(2)),
      shortfall: Math.round(shortfall),
    };
  });

  // Y-axis domains. Right axis: at least 0–100 (so all three risk bands
  // render), extended if the funding % overshoots. Rounded up to nearest
  // 10 so tick labels stay clean.
  const maxDollar = Math.max(
    1,
    ...data.map((d) => Math.max(d.reserveBalance, d.fullyFunded))
  );
  const maxActualPct = Math.max(0, ...data.map((d) => d.actualFundingPct));
  const rightMax = Math.max(100, Math.ceil(Math.max(maxActualPct, targetPct) / 10) * 10);

  // 2026-02-27 (Dave) — tightened bar sizing so grouped Reserve
  // Balance + Fully Funded bars actually render at 5y/10y horizons.
  // Previously `maxBarSize=34` × 2 bars didn't fit inside a category
  // with 50% gap → bars collapsed to 0 width and disappeared. New
  // values keep the pair readable at every horizon.
  const barCategoryGap = localHorizon <= 10 ? "22%" : "16%";
  const maxBarSize = isPanel ? (localHorizon <= 10 ? 14 : 6) : (localHorizon <= 10 ? 20 : 14);
  // Panel geometry: smaller axis strips + fonts so 30 years fit one column.
  const axisFont = isPanel ? 9 : 11;
  const leftAxisW = isPanel ? 46 : 64;
  const rightAxisW = isPanel ? 40 : 54;
  // `barGap` controls the space BETWEEN sibling bars within one
  // category — 0 makes Reserve Balance and Fully Funded sit flush
  // against each other, reading as a paired unit per year.
  const barGap = 0;

  return (
    <div
      className={isPanel ? "panel-compact flex flex-col" : "chart-modal-overlay fixed inset-0 z-50 flex items-center justify-center"}
      style={isPanel ? { height: "100%", minHeight: 0 } : { background: "rgba(20, 30, 45, 0.55)", backdropFilter: "blur(2px)", "--qc-lens-top": `${qcLensTop}px` }}
      onClick={isPanel ? undefined : onClose}
      data-testid={isPanel ? "funding-analysis-panel" : "funding-analysis-overlay"}
    >
      <div
        className={isPanel ? "flex-1 min-h-0 flex flex-col" : "chart-modal-content bg-white shadow-2xl flex flex-col"}
        style={isPanel ? undefined : {
          width: "min(1440px, 95vw)",
          height: "min(820px, 92vh)",
          borderRadius: 8,
          border: "1px solid #1C4E80",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid={isPanel ? undefined : "funding-analysis-modal"}
      >
        {isPanel && (
          <div className="dash-panel-header">
            <div className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-navy">Funding Level Analysis</div>
            <div className="text-[10.5px] text-navy font-semibold font-mono tabular-nums">{localHorizon} Year Horizon</div>
          </div>
        )}
        {/* Header — 2026-02-27 (Dave):
            3-column grid so the association name centers on the row while
            the horizon indicator right-aligns against the graph's right
            edge. Both meta labels bumped one notch (11 → 12 px), darkened
            (slate → grey) and set bold. Applied consistently across the
            three "More" menu charts (Funding Level Analysis, Cumulative
            Reserve Expenses, Reserve Spending Mix). */}
        {!isPanel && (
        <div
          className="grid items-center px-5 py-3 border-b border-rule"
          style={{ background: "#F7FAFC", gridTemplateColumns: "1fr auto 1fr" }}
        >
          <div className="text-[13px] tracking-[0.14em] uppercase font-bold text-navy justify-self-start">
            Funding Level Analysis
          </div>
          {association ? (
            <div
              className="text-[15px] font-bold text-grey justify-self-center"
              data-testid="funding-analysis-assoc-name"
            >
              {association.name}
            </div>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-3 justify-self-end">
            <span
              className="text-[13px] font-bold text-grey font-mono tabular-nums"
              data-testid="funding-analysis-horizon-label"
            >
              {localHorizon} Year Horizon
            </span>
            {view === "table" && (
              <button
                type="button"
                onClick={() => setView("chart")}
                className="border border-navy bg-white hover:bg-navy hover:text-white text-navy transition-colors rounded-[3px] px-2.5 py-0.5 text-[10.5px] font-semibold tracking-[0.14em] uppercase"
                data-testid="funding-analysis-view-chart-btn"
                title="Back to chart"
              >
                Chart
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-slate hover:text-coral transition-colors"
              data-testid="funding-analysis-close"
              title="Close (Esc)"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        )}

        {/* Body — switches between chart view and Full Funding Calculation table.
            State-preserving isolation: only one is mounted at a time so Recharts
            doesn't remeasure while hidden. */}
        {view === "table" ? (
          <FullFundingCalculationTable
            calc={calc}
            variables={variables}
            association={association}
            horizon={localHorizon}
          />
        ) : (
          <>
            {/* Manual legend row — placed above the chart so Recharts's built-in
                legend doesn't add awkward vertical padding to the plot.
                2026-02-27 (Dave) — Legend slides left; a compact Horizon
                selector sits on the right so the CPA can retune the
                window (5–30 years) without leaving the modal. */}
            {/* 2026-02-27 (Dave) — legend row is horizontally padded so its
                left edge aligns with the primary (left) Y-axis line and
                its right edge aligns with the secondary (right) Y-axis
                line of the chart below.  Values derived from the chart
                geometry: left = wrapper-pad(12) + chart-margin-left(8)
                + left-YAxis-width(64) = 84; right = wrapper-pad(12) +
                chart-margin-right(12) + right-YAxis-width(54) = 78. */}
            {/* Panel view: plot ~15% shorter; freed space pads above/below the legend. */}
            {isPanel && <div style={{ flex: `0 0 ${PANEL_LEGEND_GAP_TOP}` }} aria-hidden="true" />}
            <div
              className={"flex items-center gap-4 " + (isPanel ? "justify-center pt-1.5 pb-0.5 text-[9px]" : "justify-between pt-3 pb-1 text-[12px]")}
              style={isPanel ? { paddingLeft: 14, paddingRight: 14 } : { paddingLeft: 84, paddingRight: 78 }}
              data-testid="funding-analysis-legend"
            >
              <div className={"flex items-center flex-wrap " + (isPanel ? "flex-nowrap whitespace-nowrap justify-center gap-x-5" : "gap-5")}>
              {[
                { color: "#88BFDE", label: "Reserve Balance ($)", kind: "bar" },
                { color: "#1C4E80", label: isPanel ? "Fully Funded ($)" : "Fully Funded Balance ($)", kind: "bar" },
                { color: "#EA6A47", label: "Projected Funding %", kind: "dot" },
                { color: "#1C4E80", label: isPanel ? "Target %" : "Target Funding %", kind: "line" },
              ].map((it) => (
                <span key={it.label} className="inline-flex items-center gap-1.5 text-ink">
                  {it.kind === "bar" && (
                    <span style={{ width: isPanel ? 9 : 14, height: isPanel ? 6 : 10, background: it.color, display: "inline-block" }} />
                  )}
                  {it.kind === "line" && (
                    <span
                      style={{
                        width: 18,
                        height: 2,
                        background: it.color,
                        display: "inline-block",
                        borderRadius: 2,
                      }}
                    />
                  )}
                  {it.kind === "dot" && (
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        background: it.color,
                        border: "1px solid #fff",
                        borderRadius: "50%",
                        display: "inline-block",
                      }}
                    />
                  )}
                  {it.kind === "line-dot" && (
                    <span
                      style={{
                        width: 18,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      <span
                        style={{
                          width: 18,
                          height: 2,
                          background: it.color,
                          display: "inline-block",
                          borderRadius: 2,
                        }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          width: 5,
                          height: 5,
                          background: it.color,
                          border: "1px solid #fff",
                          borderRadius: "50%",
                        }}
                      />
                    </span>
                  )}
                  {it.kind === "dash" && (
                    <span
                      style={{
                        width: 18,
                        height: 0,
                        borderTop: `2px dashed ${it.color}`,
                        display: "inline-block",
                  }}
                />
              )}
              {it.label}
            </span>
          ))}
              </div>
              {/* Horizon selector — same 5/10/15/20/25/30 strip pattern as
                  the TopBar's horizon selector so the visual language
                  matches. Local-to-modal: doesn't change the dashboard
                  horizon behind it. */}
              {!isPanel && (
              <div
                className="flex items-center gap-2 shrink-0"
                data-testid="funding-analysis-horizon-selector-wrap"
              >
                <span
                  className="uppercase tracking-[0.14em] text-navy font-semibold"
                  style={{ fontSize: 11, lineHeight: 1 }}
                >
                  Horizon
                </span>
                <div
                  className="flex items-stretch border border-rule overflow-hidden rounded-[3px]"
                  role="tablist"
                  data-testid="funding-analysis-horizon-selector"
                >
                  {[5, 10, 15, 20, 25, 30].map((h) => {
                    const active = h === localHorizon;
                    return (
                      <button
                        key={h}
                        type="button"
                        onClick={() => setLocalHorizon(h)}
                        data-testid={`funding-analysis-horizon-${h}`}
                        className="text-[10.5px] font-mono tabular-nums transition-colors border-r border-rule last:border-r-0"
                        style={{
                          // 2026-02-28 (Dave) — one notch smaller to
                          // match the TopBar horizon strip (28×20 →
                          // 25×18) and drop 11→10.5 px font.
                          padding: "3px 0",
                          width: 25,
                          textAlign: "center",
                          background: active ? "#1C4E80" : "#fff",
                          color: active ? "#fff" : "#5A6B75",
                          fontWeight: active ? 600 : 500,
                          cursor: active ? "default" : "pointer",
                        }}
                        aria-selected={active}
                        role="tab"
                      >
                        {h}
                      </button>
                    );
                  })}
                </div>
              </div>
              )}
            </div>
            {isPanel && <div style={{ flex: `0 0 ${PANEL_LEGEND_GAP_BOTTOM}` }} aria-hidden="true" />}

        {/* Chart body — `wf-nofocus` (2026-09-27, Dave) strips the
            thick blue focus outline that Chrome/Safari paint on the
            <svg> when the user clicks any bar/dot to reveal the
            popup detail. Same class the Reserve Activity chart uses
            on the Dashboard, so the two behave identically. */}
        <div style={{ flex: 1, minHeight: 0, padding: isPanel ? "0 8px 6px" : "4px 12px 12px", position: "relative" }} className="wf-nofocus">
          {/* 2026-02-28 (Dave) — Low Reserve Balance indicator.
              Anchored top-left of the chart area (just inside the
              plot rectangle, past the left Y-axis strip so it doesn't
              overlay the axis labels).  Uses the same coral-outlined
              pill treatment as the Projections callouts so the CPA
              gets the same low-water-mark story wherever they look.
              Data lives in the same `data` array the chart itself
              renders, so the number is always in sync with the
              horizon selector. */}
          {(() => {
            const lowYear = calc.display?.horizons?.[String(data.length)]?.low_balance_year;
            const low = data.find((d) => d.year === lowYear) || null;
            if (!low) return null;
            return (
              <div
                data-testid="funding-analysis-low-balance"
                style={{
                  // 2026-09-27 (Dave, v3) — nudged right by ~3
                  // characters (11 px font · ~7 px per char = 21 px)
                  // per user's spot-check: the callout was landing
                  // flush against the plot border and reading
                  // cramped. New left: 78 + 21 = 99.
                  position: "absolute",
                  top: isPanel ? 10 : 16,
                  left: isPanel ? 8 + 4 + leftAxisW + 8 : 99,
                  zIndex: 2,
                  padding: 0,
                  fontSize: isPanel ? 9.5 : 11,
                  color: "#1C4E80",
                  whiteSpace: "nowrap",
                }}
              >
                Low Reserve Balance in Year{" "}
                <span style={{ fontWeight: 700 }}>{low.year}</span>
                {" = "}
                <span style={{ fontWeight: 700 }}>
                  {fmtCurrency(Math.round(low.reserveBalance || 0))}
                </span>
              </div>
            );
          })()}
          <ResponsiveContainer>
            <ComposedChart
              data={data}
              margin={{ top: 8, right: isPanel ? 4 : 12, bottom: 4, left: isPanel ? 4 : 8 }}
              barCategoryGap={barCategoryGap}
              barGap={barGap}
              maxBarSize={maxBarSize}
              onClick={handleFAChartClick}
            >
              <CartesianGrid stroke="#E8ECF1" vertical={false} />

              {/* Risk bands — right (%) axis. Order matters: paint deepest
                  first, then layer up. Opacity darkened 2026-02-26 (Dave)
                  from 0.09 → 0.18 so the risk zones read at a glance
                  without drowning out the foreground series. */}
              <ReferenceArea
                yAxisId="right"
                y1={0}
                y2={33}
                fill="#EA6A47"
                fillOpacity={0.18}
                ifOverflow="hidden"
              />
              <ReferenceArea
                yAxisId="right"
                y1={33}
                y2={66}
                fill="#F4D35E"
                fillOpacity={0.18}
                ifOverflow="hidden"
              />
              <ReferenceArea
                yAxisId="right"
                y1={66}
                y2={100}
                fill="#83C88F"
                fillOpacity={0.18}
                ifOverflow="hidden"
              />

              {/* 2026-02-28 (Dave) — TOP border of the plot area.
                  The left/right YAxes and bottom XAxis all paint a
                  1-px black axis line, but Recharts renders nothing
                  on the top edge, leaving the green risk band bleeding
                  into whitespace.  A ReferenceLine at the top of the
                  right-axis domain closes the rectangle so all four
                  sides have matching hairline stroke. */}
              <ReferenceLine
                yAxisId="right"
                y={rightMax}
                stroke="#1A1A1A"
                strokeWidth={1}
                ifOverflow="visible"
              />

              <XAxis
                dataKey="year"
                tick={{ fill: "#1A1A1A", fontSize: isPanel ? 8.5 : 10, fontFamily: "IBM Plex Mono" }}
                axisLine={{ stroke: "#1A1A1A" }}
                tickLine={{ stroke: "#1A1A1A" }}
                interval={isPanel && localHorizon > 15 ? 1 : 0}
                angle={-90}
                textAnchor="end"
                height={isPanel ? 34 : 48}
                dy={4}
              />
              {/* LEFT axis — dollars */}
              <YAxis
                yAxisId="left"
                orientation="left"
                tick={{ fill: "#1A1A1A", fontSize: axisFont, fontFamily: "IBM Plex Mono" }}
                axisLine={{ stroke: "#1A1A1A" }}
                tickLine={{ stroke: "#1A1A1A" }}
                tickFormatter={fmtCurrencyK}
                width={leftAxisW}
                domain={[0, "auto"]}
                label={{
                  value: "Balance ($)",
                  angle: -90,
                  position: "insideLeft",
                  offset: isPanel ? 0 : 6,
                  style: { textAnchor: "middle", fill: "#1C4E80", fontSize: axisFont, fontWeight: 700, letterSpacing: "0.08em" },
                }}
              />
              {/* RIGHT axis — percentages */}
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fill: "#1A1A1A", fontSize: axisFont, fontFamily: "IBM Plex Mono" }}
                axisLine={{ stroke: "#1A1A1A" }}
                tickLine={{ stroke: "#1A1A1A" }}
                tickFormatter={(v) => `${v}%`}
                width={rightAxisW}
                domain={[0, rightMax]}
                label={{
                  value: "Funding %",
                  angle: 90,
                  position: "insideRight",
                  offset: isPanel ? 0 : 6,
                  style: { textAnchor: "middle", fill: "#1C4E80", fontSize: axisFont, fontWeight: 700, letterSpacing: "0.08em" },
                }}
              />

              <Tooltip
                /* 2026-09-27 (Dave) — Click-to-reveal parity with the
                   Dashboard Reserve Activity chart. Hover renders only
                   the light-gray column tint (`cursor` fill below);
                   detail popup appears ONLY for the year the user has
                   explicitly clicked (`revealedLabel`). Rendered as a
                   JSX element (not a function) so Recharts identifies
                   <Tooltip>'s child type correctly — matches the
                   ActivityChart pattern exactly. */
                content={
                  <DelayedTooltipContent
                    inner={<FundingAnalysisTooltip revealedLabel={revealedLabel} />}
                  forceLabel={revealedLabel}
                  />
                }
                cursor={{ fill: "rgba(148, 163, 184, 0.20)" }}
                wrapperStyle={{ pointerEvents: "none" }}
                trigger="hover"
                isAnimationActive={false}
              />

              {/* Reserve Balance columns — LEFT axis */}
              <Bar
                yAxisId="left"
                dataKey="reserveBalance"
                name="Reserve Balance"
                fill="#88BFDE"
                isAnimationActive={false}
              />

              {/* Fully Funded Balance columns — LEFT axis.
                  2026-02-26 (Dave) — converted from a Line-with-dots
                  to a Bar so it reads as "target dollars per year"
                  next to the Reserve Balance bar. Recharts groups
                  Bars on the same yAxisId side-by-side automatically,
                  giving the desired "actual vs fully-funded" pair per
                  year. Deep navy fill contrasts cleanly with the pale
                  sky Reserve Balance next to it. Funding Level
                  Shortfall series removed in the same pass — the pair
                  now tells the same story without a third column
                  cluttering the group. */}
              <Bar
                yAxisId="left"
                dataKey="fullyFunded"
                name="Fully Funded Balance"
                fill="#1C4E80"
                isAnimationActive={false}
              />

              {/* Projected Funding % — RIGHT axis. Dot markers only
                  (no connecting line) with the whole-percent value
                  labelled above each marker. 2026-02-27 (Dave) —
                  renamed from "Actual Funding %" and bumped dot +
                  label one notch (r: 3 → 4, fontSize: 10 → 12).
                  2026-02-27 (Dave · v2) — label font trimmed back
                  one notch (12 → 11) per readability review.
                  2026-02-28 (Dave) — dot + label shifted LEFT so
                  they hover directly over the Reserve Balance
                  column (the leftmost of the two bars in each
                  year's group).  With `barGap=0` and `maxBarSize`
                  driving bar width, the leftmost bar's center sits
                  `maxBarSize / 2` pixels to the LEFT of the year's
                  category tick, so that's the exact offset we
                  apply here to both the dot and its label. */}
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="actualFundingPct"
                name="Projected Funding %"
                stroke="none"
                dot={(props) => {
                  const { cx, cy, index } = props;
                  if (cx == null || cy == null) return null;
                  return (
                    <circle
                      key={`fund-dot-${index}`}
                      cx={cx - (maxBarSize / 2 + 3)}
                      cy={cy}
                      r={isPanel ? 2.5 : 4}
                      fill="#EA6A47"
                      stroke="#fff"
                      strokeWidth={1}
                    />
                  );
                }}
                activeDot={false}
                label={isPanel ? false : {
                  position: "top",
                  offset: 8,
                  dx: -(maxBarSize / 2 + 3),
                  fill: "#EA6A47",
                  fontSize: 11,
                  fontWeight: 800,
                  fontFamily: "IBM Plex Mono",
                  formatter: (v) =>
                    v == null || Number.isNaN(v) ? "" : `${Math.round(v)}%`,
                }}
                isAnimationActive={false}
              />

              {/* Target Funding % reference line — RIGHT axis.
                  2026-02-27 (Dave) — label removed at Dave's request so
                  the axis strip stays clean; the target is still
                  disclosed in the legend chip above and (visually) by
                  the horizontal navy line itself. */}
              <ReferenceLine
                yAxisId="right"
                y={targetPct}
                stroke="#1C4E80"
                strokeWidth={1.6}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Bottom-left toggle button — Dave 2026-08-05 spec. Opens the
            Full Funding Calculation subview (element-by-element table). */}
        {!isPanel && (
        <div
          className="flex items-center px-5 py-3 border-t border-rule"
          data-testid="funding-analysis-footer"
          style={{ background: "#F7FAFC" }}
        >
          <button
            type="button"
            onClick={() => setView("table")}
            className="border border-navy bg-white hover:bg-navy hover:text-white text-navy transition-colors rounded-[3px] px-3 py-1 text-[11px] font-semibold tracking-[0.14em] uppercase"
            data-testid="view-ffc-btn"
          >
            View Full Funding Calculation
          </button>
        </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}
